import type { ReadOnlyStore } from "./ReadOnlyStore";

export interface User {
  id: string;
  email: string;
  name: string;
  cognitoSub: string;
}

export interface Site {
  id: string;
  name: string;
  region?: string;
  permissions: string[];
}

export interface Cage {
  id: string;
  siteId: string;
  name: string;
  position?: number;
}

export interface Permission {
  name: string;
  active: boolean;
  sites: number[];
}

export type ToastLevel = "info" | "success" | "warning" | "error";

export type ShellMode = "shell" | "standalone";

export interface PageMeta {
  title: string;
  icon: string | null;
  withDates: boolean;
  hideSitePicker: boolean;
  isSingleSite: boolean;
}

export const DEFAULT_PAGE_META: PageMeta = {
  title: "",
  icon: null,
  withDates: false,
  hideSitePicker: false,
  isSingleSite: false,
};

export interface ShellHandle {
  auth: {
    getToken(): Promise<string>;
    onTokenRefresh(cb: (token: string) => void): () => void;
    currentUser: ReadOnlyStore<User | null>;
  };
  permissions: {
    has(perm: string): boolean;
    list(): string[];
    raw(): Permission[];
    onChange(cb: () => void): () => void;
  };
  sites: {
    list: ReadOnlyStore<Site[]>;
    selectedSiteId: ReadOnlyStore<string | null>;
    setSelectedSite(id: string): void;
    refresh(): Promise<void>;
    selectedSiteIds: ReadOnlyStore<number[]>;
    setSelectedSiteIds(ids: number[]): void;
    selectedCageIds: ReadOnlyStore<string[]>;
    setSelectedCageIds(ids: string[]): void;
  };
  dateRange: {
    range: ReadOnlyStore<{ start: string | null; end: string | null }>;
    setRange(start: string | null, end: string | null): void;
  };
  pageMeta: {
    meta: ReadOnlyStore<PageMeta>;
    set(meta: Partial<PageMeta>): void;
    clear(): void;
  };
  cages: {
    listForSite(siteId: string): ReadOnlyStore<Cage[]>;
    refresh(siteId: string): Promise<void>;
  };
  nav: {
    navigate(path: string, opts?: { replace?: boolean }): void;
    back(): void;
    currentPath: ReadOnlyStore<string>;
  };
  i18n: {
    language: ReadOnlyStore<string>;
    setLanguage(lang: string): void;
  };
  notify(msg: string, level: ToastLevel): void;
  mode: ShellMode;
  /**
   * True when the shell is running on a cached offline snapshot: the cloud is
   * unreachable, permissions and sites came from disk, and cloud-backed data is
   * empty rather than merely still loading.
   */
  offline: ReadOnlyStore<boolean>;
  /**
   * When the offline session ends, from the snapshot it was hydrated from.
   * Null while online: nothing has recorded a deadline.
   */
  offlineExpiresAt: ReadOnlyStore<number | null>;
  /**
   * True when Cognito answered but the rest of the cloud did not, so the
   * session is running on cached permissions with a live token. Distinct from
   * `offline`, which is the whole link being down: only one of the two can be
   * true, and the difference is what the UI says went wrong.
   */
  offlineDegraded: ReadOnlyStore<boolean>;
  topbarSlots: {
    /** Shell-owned: DOM node MFEs portal their content into. */
    left: ReadOnlyStore<HTMLElement | null>;
    center: ReadOnlyStore<HTMLElement | null>;
    setLeft(el: HTMLElement | null): void;
    setCenter(el: HTMLElement | null): void;
    /** MFE-owned: tells shell to hide its own fallback rendering for that slot. */
    leftClaimed: ReadOnlyStore<boolean>;
    centerClaimed: ReadOnlyStore<boolean>;
    setLeftClaimed(v: boolean): void;
    setCenterClaimed(v: boolean): void;
  };
}
